import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { api, fmtNum } from "@/api";
import { TestTube, Play } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const INTERVALS_BY_MODE = {
  scalp: ["1m", "5m", "15m"],
  swing: ["15m", "1h", "4h"],
  position: ["1h", "4h", "1D"],
  macro: ["4h", "1D", "1W"],
};
const DAYS = [30, 60, 90, 180];

export function BacktestPanel({ defaultSymbol = "BTCUSDT", pairs = [] }) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [interval, setInterval] = useState("5m");
  const [days, setDays] = useState(30);
  const [minScore, setMinScore] = useState(60);
  const [direction, setDirection] = useState("long");
  const [mode, setMode] = useState("scalp");

  // Auto-align default interval when mode changes
  const changeMode = (m) => {
    setMode(m);
    if (m === "scalp") setInterval("5m");
    else if (m === "swing") setInterval("1h");
    else if (m === "position") setInterval("4h");
    else if (m === "macro") setInterval("1D");
  };

  const backtest = useMutation({
    mutationFn: () => api.backtest({ symbol, interval, days, min_score: minScore, direction, mode }),
  });

  const result = backtest.data;
  const equity = result?.equity_curve || [];

  const winRate = result?.win_rate ?? 0;
  const winColor = winRate >= 55 ? "#00FF66" : winRate >= 45 ? "#F5C518" : "#FF3B30";

  return (
    <div data-testid="backtest-panel" className="border border-white/10 bg-[#0A0A0A] h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 flex flex-wrap items-center gap-3 bg-[#0f0f0f]">
        <TestTube size={16} className="text-[#F5C518]" />
        <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Strategy Backtest</h2>

        <select
          data-testid="backtest-symbol"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          className="bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px] ml-2 max-w-[160px]"
          title={`${pairs.length} pairs available`}
        >
          {(pairs.length ? pairs : [{ symbol: "BTCUSDT" }, { symbol: "ETHUSDT" }, { symbol: "SOLUSDT" }]).map((p) => (
            <option key={p.symbol} value={p.symbol}>{p.symbol}</option>
          ))}
        </select>

        <div className="flex border border-white/10" data-testid="backtest-mode">
          {[
            { k: "scalp", label: "Scalp" },
            { k: "swing", label: "Swing" },
            { k: "position", label: "Position" },
            { k: "macro", label: "Macro" },
          ].map((m) => (
            <button
              key={m.k}
              data-testid={`backtest-mode-${m.k}`}
              onClick={() => changeMode(m.k)}
              className={`px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest ${
                mode === m.k ? "bg-[#F5C518] text-black" : "text-white/60 hover:bg-white/5"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="flex border border-white/10">
          {(INTERVALS_BY_MODE[mode] || INTERVALS_BY_MODE.scalp).map((i) => (
            <button
              key={i}
              data-testid={`backtest-interval-${i}`}
              onClick={() => setInterval(i)}
              className={`px-2.5 py-1 font-mono text-[11px] uppercase ${interval === i ? "bg-white text-black" : "text-white/60 hover:bg-white/5"}`}
            >
              {i}
            </button>
          ))}
        </div>

        <div className="flex border border-white/10" data-testid="backtest-dir">
          {["long", "short"].map((d) => (
            <button
              key={d}
              data-testid={`backtest-dir-${d}`}
              onClick={() => setDirection(d)}
              className={`px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest ${
                direction === d
                  ? d === "long"
                    ? "bg-[#00FF66] text-black"
                    : "bg-[#FF3B30] text-black"
                  : "text-white/60 hover:bg-white/5"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="flex border border-white/10">
          {DAYS.map((d) => (
            <button
              key={d}
              data-testid={`backtest-days-${d}`}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 font-mono text-[11px] ${days === d ? "bg-white text-black" : "text-white/60 hover:bg-white/5"}`}
            >
              {d}d
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 font-mono text-[11px] text-white/60">
          Min score
          <input
            data-testid="backtest-min-score"
            type="number"
            value={minScore}
            min={40}
            max={95}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-14 bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px]"
          />
        </label>

        <Button
          data-testid="run-backtest-btn"
          onClick={() => backtest.mutate()}
          disabled={backtest.isPending}
          className="ml-auto rounded-none bg-[#00FF66] hover:bg-[#00e65b] text-black font-mono text-[11px] uppercase tracking-widest px-3 py-1 h-auto"
        >
          <Play size={12} weight="fill" className="mr-1" />
          {backtest.isPending ? "Running…" : "Run Backtest"}
        </Button>
      </div>

      {backtest.isError && (
        <div className="p-4 text-[#FF3B30] font-mono text-xs">
          Backtest failed: {String(backtest.error?.response?.data?.detail || backtest.error?.message || "unknown")}
        </div>
      )}

      {!result && !backtest.isPending && (
        <div className="flex-1 flex items-center justify-center text-center px-6">
          <div className="max-w-md">
            <div className="font-display text-white text-lg mb-2">Test the confluence engine</div>
            <p className="text-white/50 text-sm font-mono">
              10-criteria long-entry strategy: HTF bias, EMA stack, Supertrend, RSI bounce, unfilled FVG,
              volume spike, OBV, bullish divergence, double bottom, EMA20 basis. Fees 0.05% per side.
            </p>
          </div>
        </div>
      )}

      {result && (
        <div className="flex-1 flex flex-col p-4 gap-4 overflow-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="backtest-metrics">
            <Metric label="Trades" value={result.total_signals} />
            <Metric label="Win Rate" value={`${winRate}%`} color={winColor} />
            <Metric label="Wins" value={result.wins} color="#00FF66" />
            <Metric label="Losses" value={result.losses} color="#FF3B30" />
            <Metric label="Avg R:R" value={result.avg_rr} />
            <Metric
              label={`Lev PnL (${days}d)`}
              value={`${result.total_leveraged_pnl_pct}%`}
              color={result.total_leveraged_pnl_pct >= 0 ? "#00FF66" : "#FF3B30"}
            />
          </div>

          <div className="flex-1 min-h-[220px] border border-white/10 bg-[#0A0A0A] p-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equity}>
                <defs>
                  <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00FF66" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="time"
                  hide
                />
                <YAxis
                  domain={["auto", "auto"]}
                  tickFormatter={(v) => `${((v / 100) - 1) * 100 > 0 ? "+" : ""}${(v - 100).toFixed(1)}%`}
                  stroke="rgba(255,255,255,0.4)"
                  fontSize={10}
                  width={54}
                  tick={{ fontFamily: "JetBrains Mono" }}
                />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #333", fontSize: 11, fontFamily: "JetBrains Mono" }}
                  formatter={(v) => [`${v.toFixed(2)}`, "Equity"]}
                  labelFormatter={(l) => new Date(l).toLocaleString()}
                />
                <ReferenceLine y={100} stroke="rgba(255,255,255,0.3)" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="equity" stroke="#00FF66" strokeWidth={2} fill="url(#pnlGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="max-h-56 overflow-auto border border-white/10">
            <table className="w-full text-left">
              <thead className="text-[10px] font-mono uppercase text-white/50 sticky top-0 bg-[#0f0f0f]">
                <tr>
                  <th className="px-3 py-2">Entry</th>
                  <th className="px-3 py-2 text-right">Score</th>
                  <th className="px-3 py-2 text-right">Lev</th>
                  <th className="px-3 py-2 text-right">Entry Px</th>
                  <th className="px-3 py-2 text-right">Exit Px</th>
                  <th className="px-3 py-2">Outcome</th>
                  <th className="px-3 py-2 text-right">Lev PnL %</th>
                </tr>
              </thead>
              <tbody>
                {result.trades.slice().reverse().map((t, i) => (
                  <tr key={i} className="border-b border-white/5" data-testid={`bt-trade-${i}`}>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-white/70">
                      {new Date(t.entry_time).toISOString().replace("T", " ").slice(0, 16)}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{t.score}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-right text-[#F5C518]">{t.leverage}×</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{fmtNum(t.entry_price)}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{fmtNum(t.exit_price)}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
                      <span
                        className={
                          t.outcome === "win"
                            ? "text-[#00FF66]"
                            : t.outcome === "loss"
                            ? "text-[#FF3B30]"
                            : "text-white/60"
                        }
                      >
                        {t.outcome}
                      </span>
                    </td>
                    <td
                      className={`px-3 py-1.5 font-mono text-[11px] text-right ${t.leveraged_pnl_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3B30]"}`}
                    >
                      {t.leveraged_pnl_pct >= 0 ? "+" : ""}{t.leveraged_pnl_pct}%
                    </td>
                  </tr>
                ))}
                {result.trades.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center font-mono text-xs text-white/40">
                      No signals triggered on {symbol} @ {interval} over last {days}d with score ≥ {minScore}. Try lowering the threshold.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color = "#FFFFFF" }) {
  return (
    <div className="border border-white/10 bg-[#0f0f0f] px-3 py-2">
      <div className="text-white/40 font-mono text-[10px] uppercase tracking-widest">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
